import { useEffect, useRef, useState } from "react";
import { FiSun, FiMoon, FiMonitor, FiDroplet, FiChevronRight, FiChevronDown, FiCheck } from "react-icons/fi";
import { useTheme, SCREEN_STYLES, ACCENT_PALETTES } from "../context/ThemeContext";

const STYLE_ICON = { light: FiSun, dark: FiMoon, auto: FiMonitor };

export default function StyleMenu() {
  const { screenStyle, setScreenStyle, accent, setAccent, theme } = useTheme();
  const [open, setOpen] = useState(false);
  const [styleOpen, setStyleOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const wrapRef = useRef(null);
  const styleRowRef = useRef(null);
  const closeTimer = useRef(null);
  const hoverOpenedAt = useRef(0);
  const styleOpenRef = useRef(false);

  const closeAll = () => {
    setOpen(false);
    styleOpenRef.current = false;
    setStyleOpen(false);
    setPaletteOpen(false);
  };

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) closeAll(); };
    const onKey = (e) => { if (e.key === "Escape") closeAll(); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  useEffect(() => () => clearTimeout(closeTimer.current), []);

  const setStyles = (value) => {
    styleOpenRef.current = value;
    setStyleOpen(value);
  };
  const showStyles = () => {
    clearTimeout(closeTimer.current);
    if (!styleOpenRef.current) hoverOpenedAt.current = Date.now();
    setStyles(true);
  };
  const clickStyles = () => {
    setPaletteOpen(false);
    if (styleOpenRef.current && Date.now() - hoverOpenedAt.current < 500) return;
    setStyles(!styleOpenRef.current);
  };
  const onEnter = (e) => { if (e.pointerType === "mouse") showStyles(); };
  const onLeave = (e) => { if (e.pointerType === "mouse") hideStylesSoon(); };
  const hideStylesSoon = () => { clearTimeout(closeTimer.current); closeTimer.current = setTimeout(() => setStyles(false), 180); };

  const current = SCREEN_STYLES.find((s) => s.key === screenStyle) || SCREEN_STYLES[0];
  const currentAccent = ACCENT_PALETTES.find((a) => a.key === accent) || ACCENT_PALETTES[0];
  const TriggerIcon = theme === "dark" ? FiMoon : FiSun;
  const CurrentStyleIcon = STYLE_ICON[current.key];

  const pickStyle = (key) => {
    setScreenStyle(key);
    setStyles(false);
    styleRowRef.current?.focus();
  };

  return (
    <div className="topbar-pop style-menu" ref={wrapRef}>
      <button
        type="button"
        className="icon-btn style-trigger"
        onClick={() => (open ? closeAll() : setOpen(true))}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={`Screen style and accent palette (currently ${current.label}, ${currentAccent.label})`}
        title="Screen style and accent palette"
      >
        <TriggerIcon size={18} aria-hidden="true" />
        <span className="style-trigger-dot" style={{ background: "var(--pms-primary)" }} aria-hidden="true" />
      </button>

      {open && (
        <div className="popover style-panel">
          <p className="style-panel-title">Personalise</p>

          <div className="style-row-wrap" onPointerEnter={onEnter} onPointerLeave={onLeave}>
            <button
              type="button"
              ref={styleRowRef}
              className={`style-row ${styleOpen ? "is-open" : ""}`}
              aria-haspopup="true"
              aria-expanded={styleOpen}
              onClick={clickStyles}
              onKeyDown={(e) => { if (e.key === "ArrowRight" || e.key === "ArrowLeft") { e.preventDefault(); showStyles(); } }}
            >
              <FiMonitor size={16} aria-hidden="true" />
              <span className="style-row-label">Screen style</span>
              <span className="style-row-value"><CurrentStyleIcon size={13} aria-hidden="true" /> {current.label}</span>
              <FiChevronRight size={15} className="style-row-chevron" aria-hidden="true" />
            </button>

            {styleOpen && (
              <div className="style-flyout" role="radiogroup" aria-label="Screen style" onPointerEnter={onEnter} onPointerLeave={onLeave}>
                {SCREEN_STYLES.map((s) => {
                  const Icon = STYLE_ICON[s.key];
                  const selected = s.key === screenStyle;
                  return (
                    <button
                      key={s.key}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      className={`style-option ${selected ? "is-selected" : ""}`}
                      onClick={() => pickStyle(s.key)}
                    >
                      <Icon size={16} aria-hidden="true" />
                      <span className="style-option-text">
                        <strong>{s.label}</strong>
                        <small>{s.hint}</small>
                      </span>
                      {selected && <FiCheck size={15} className="style-option-check" aria-hidden="true" />}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <button
            type="button"
            className={`style-row ${paletteOpen ? "is-open" : ""}`}
            aria-expanded={paletteOpen}
            onClick={() => { setStyles(false); setPaletteOpen((v) => !v); }}
          >
            <FiDroplet size={16} aria-hidden="true" />
            <span className="style-row-label">Accent palette</span>
            <span className="style-row-value">
              <span className="swatch-mini" style={{ background: currentAccent.swatch }} aria-hidden="true" /> {currentAccent.label}
            </span>
            <FiChevronDown size={15} className="style-row-chevron" aria-hidden="true" />
          </button>

          {paletteOpen && (
            <div className="swatch-grid" role="radiogroup" aria-label="Accent palette">
              {ACCENT_PALETTES.map((p) => {
                const selected = p.key === accent;
                return (
                  <button
                    key={p.key}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    className={`swatch ${selected ? "is-selected" : ""}`}
                    onClick={() => setAccent(p.key)}
                    title={p.label}
                  >
                    <span className="swatch-dot" style={{ background: p.swatch }}>
                      {selected && <FiCheck size={14} aria-hidden="true" />}
                    </span>
                    <span className="swatch-name">{p.label}</span>
                  </button>
                );
              })}
            </div>
          )}

          <p className="style-panel-note">Saved on this browser only.</p>
        </div>
      )}
    </div>
  );
}
